if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        this.enableMouseDrag();
      }

      /* WILD : glisser les images a la souris (ordinateur uniquement).
         Le tactile (mobile / tablette) garde son comportement natif. */
      enableMouseDrag() {
        const viewer = this.elements.viewer;
        if (!viewer || !this.dataset.desktopLayout || !this.dataset.desktopLayout.includes('thumbnail')) return;

        if (!document.getElementById('wild-gallery-drag-css')) {
          const style = document.createElement('style');
          style.id = 'wild-gallery-drag-css';
          style.textContent =
            '@media (min-width: 750px) {' +
            '.wild-gallery-draggable { cursor: grab; }' +
            '.wild-gallery-draggable.wild-gallery-dragging { cursor: grabbing; }' +
            '.wild-gallery-draggable .product__media-item img { -webkit-user-drag: none; user-select: none; }' +
            '}';
          document.head.appendChild(style);
        }
        viewer.classList.add('wild-gallery-draggable');

        const THRESHOLD = 50;
        let startX = 0;
        let startY = 0;
        let active = false;
        let didDrag = false;

        const onMove = (event) => {
          if (!active) return;
          if (Math.abs(event.clientX - startX) > 8) {
            didDrag = true;
            viewer.classList.add('wild-gallery-dragging');
          }
        };

        const onUp = (event) => {
          if (!active) return;
          active = false;
          document.removeEventListener('pointermove', onMove);
          document.removeEventListener('pointerup', onUp);
          document.removeEventListener('pointercancel', onUp);
          viewer.classList.remove('wild-gallery-dragging');
          const dx = event.clientX - startX;
          const dy = event.clientY - startY;
          if (Math.abs(dx) >= THRESHOLD && Math.abs(dx) > Math.abs(dy)) this.goToSibling(dx < 0 ? 1 : -1);
        };

        viewer.addEventListener('pointerdown', (event) => {
          if (event.pointerType !== 'mouse' || event.button !== 0 || !this.mql.matches) return;
          if (event.target.closest('product-model, model-viewer, video, iframe, .slider-button, .product__xr-button')) return;
          active = true;
          didDrag = false;
          startX = event.clientX;
          startY = event.clientY;
          document.addEventListener('pointermove', onMove);
          document.addEventListener('pointerup', onUp);
          document.addEventListener('pointercancel', onUp);
        });

        /* Apres un glisser, on bloque le clic (evite d'ouvrir le zoom par erreur) */
        viewer.addEventListener(
          'click',
          (event) => {
            if (!didDrag) return;
            event.preventDefault();
            event.stopPropagation();
            didDrag = false;
          },
          true
        );

        viewer.addEventListener('dragstart', (event) => event.preventDefault());
      }

      goToSibling(direction) {
        const items = Array.from(this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]'));
        if (items.length < 2) return;

        let index = items.findIndex((item) => item.classList.contains('is-active'));
        const slider = this.elements.viewer.slider;
        if (this.dataset.desktopLayout === 'thumbnail_slider' && slider) {
          let min = Infinity;
          items.forEach((item, i) => {
            const distance = Math.abs(item.offsetLeft - slider.scrollLeft);
            if (distance < min) {
              min = distance;
              index = i;
            }
          });
        }
        if (index === -1) index = 0;

        const target = items[index + direction];
        if (!target) return;
        this.setActiveMedia(target.dataset.mediaId);
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
